/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import {
  GeoJSON,
  Marker,
  Polygon,
  Popup,
  Tooltip,
  useMap,
} from 'react-leaflet';
import * as L from 'leaflet';
import { useTerritoryPlanning } from '../../hooks/useTerritoryPlanning';
import _ from 'lodash';
import PopupContent from '../../components/PopupContent';
import './DanoneTerritoryGeojson.scss';
import { encrypt } from '../../../../../utils/encrypt';
import { useDanoneLegend } from '@bvt-features/danone/legend/hooks/useDanoneLegend';
import { markerIcon, markerLegend, polygonLegend } from './icon';
import MarkerShadow from '@bvt-features/danone/assets/marker/marker-shadow.png';
import areaInfo from '@bvt-features/danone/assets/legend/area-info.png';
import {
  getCategoryTerritory,
  getBorderColorByHousehold,
  getFeatureProperties,
  getFeatureStyle,
  getFillColorByHousehold,
} from './utils';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { renderToStaticMarkup } from 'react-dom/server';

const getAHSMarkerIcon = (category) => {
  const iconUrl = markerIcon[category] ?? markerIcon['AHS Reguler'];

  return L.icon({
    iconUrl,
    iconSize: [36, 42],
    iconAnchor: [18, 40],
  });
};

const getRTMMarkerIcon = (category) => {
  const iconUrl = markerIcon[category] ?? markerIcon['AHS Reguler'];

  return L.icon({
    iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],

    shadowUrl: MarkerShadow,
    shadowSize: [22, 10],
    shadowAnchor: [11, 3],
  });
};

export const DanonePOICluster = ({ pois = [] }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || pois.length === 0) {
      return;
    }

    const markerClusterGroup = L.markerClusterGroup({
      animate: false,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 32,
      elementsPlacementStrategy: 'concentric',
      elementsMultiplier: 1,
      spiderfyDistanceMultiplier: 1.2,
      firstCircleElements: 5,
      animateAddingMarkers: false,
      iconCreateFunction: (icfParam) => {
        const multiplier = map.getZoom() > 16 ? 8 : 3;
        const totalMarkerPerCluster = icfParam.getChildCount();
        const percentageOfTotalMarker =
          (totalMarkerPerCluster / pois.length) * 100;
        const opacityMultiplier = (percentageOfTotalMarker / 10) * multiplier;
        const sizeMultiplier = opacityMultiplier > 0.9 ? 1.3 : 0.8;

        const baseStyle = {
          width: 52 * sizeMultiplier,
          height: 52 * sizeMultiplier,
          borderRadius: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `rgba(229,73,52,1)`,
          opacity: 1,
        };
        const htmlElm = renderToStaticMarkup(
          <div
            style={{
              ...baseStyle,
              width: 42 * sizeMultiplier,
              height: 42 * sizeMultiplier,
              background: `rgba(229,73,52,0.5)`,
            }}
          >
            <div
              style={{
                ...baseStyle,
                width: 32 * sizeMultiplier,
                height: 32 * sizeMultiplier,
              }}
            >
              <div style={{ color: '#fff', fontSize: '1rem', fontWeight: 600 }}>
                {totalMarkerPerCluster}
              </div>
            </div>
          </div>
        );

        return L.divIcon({ html: htmlElm, iconAnchor: { x: 22, y: 22 } });
      },
    });

    pois.forEach((feature) => {
      const props = feature.properties || {};
      const category = props.segment_name ?? 'AHS Reguler';
      const lat = feature.geometry.coordinates[1];
      const lng = feature.geometry.coordinates[0];

      const icon = getRTMMarkerIcon(category);

      const marker = L.marker([lat, lng], { icon });

      markerClusterGroup.addLayer(marker);
    });

    map.addLayer(markerClusterGroup);

    // eslint-disable-next-line consistent-return
    return () => {
      map.removeLayer(markerClusterGroup);
    };
  }, [map, pois]);

  return null;
};

const DanonePOICollection = ({ pois, selectedId, setSelectedId }) => {
  return (
    <>
      {pois.map((feature) => {
        const category = feature.properties?.category;
        const icon = getAHSMarkerIcon(category);
        const position = [
          feature.geometry.coordinates[1],
          feature.geometry.coordinates[0],
        ];

        return (
          <Marker
            eventHandlers={{
              popupclose: () => {
                setSelectedId(null);
              },
              click: () => {
                setSelectedId(feature.properties.Customer_Id);
              },
            }}
            icon={icon}
            key={`${feature.properties.Customer_Id}-${feature.properties.bill_nickname}`}
            position={position}
          >
            {!selectedId && (
              <Tooltip
                className='DanoneTerritoryGeojson__tooltip-container'
                direction='top'
                sticky
              >
                <PopupContent
                  properties={getFeatureProperties(feature, 'POI')}
                  type='poi'
                />
              </Tooltip>
            )}
            <Popup offset={[0, -20]}>
              <PopupContent
                click={true}
                href={`/app/map/map-analytic/danone-area-profiling?form=${encrypt(
                  JSON.stringify({
                    customer_id: feature.properties.Customer_Id,
                  })
                )}`}
                properties={getFeatureProperties(feature, 'POI')}
                type='poi'
              />
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

const DanoneAreaGeojson = ({ data, selectedId }) => {
  return (
    <GeoJSON data={data} style={(feature) => getFeatureStyle(feature)}>
      {!selectedId && (
        <Tooltip
          className='DanoneTerritoryGeojson__tooltip-container'
          direction='top'
          sticky
        >
          <PopupContent
            properties={getFeatureProperties(data, 'Area')}
            type='area'
          />
        </Tooltip>
      )}
    </GeoJSON>
  );
};

const DanoneCatchmentGeojson = ({ data, style, setSelectedId, selectedId }) => {
  const getFeatureId = (feature) =>
    feature.properties?.cluster_id ?? feature.id;
  return (
    <>
      {data.map((feature, idx) => {
        const id = getFeatureId(feature);
        const isSelected = selectedId === id;
        const properties = getFeatureProperties(feature, 'Territory');
        const borderColor = getBorderColorByHousehold(
          feature.properties?.jumlah_ahs || 0
        );
        const fillColor = getFillColorByHousehold(
          feature.properties?.jumlah_ahs || 0
        );
        return (
          <Polygon
            eventHandlers={{
              click: (e) => {
                setSelectedId(id);
                e.target.bringToFront();
              },
              mouseover: (e) => {
                if (!selectedId) {
                  e.target.setStyle({
                    weight: 4,
                    color: borderColor,
                  });
                  e.target.bringToFront();
                }
              },
              mouseout: (e) => {
                if (!selectedId) {
                  e.target.setStyle({
                    weight: 2,
                    color: fillColor,
                  });
                }
              },
              popupopen: (e) => {
                setSelectedId(id);
                e.target.bringToFront();
              },
              popupclose: () => {
                setSelectedId(null);
              },
            }}
            key={idx}
            pathOptions={{
              ...style(feature),
              weight: isSelected ? 4 : 2,
              color: isSelected ? borderColor : fillColor,
              opacity: 1,
            }}
            positions={feature.geometry.coordinates[0].map(([lng, lat]) => [
              lat,
              lng,
            ])}
          >
            {!selectedId && (
              <Tooltip
                className='DanoneTerritoryGeojson__tooltip-container'
                direction='top'
                sticky
              >
                <PopupContent
                  properties={{
                    ...properties,
                    village_code: properties?.village_code?.length,
                  }}
                  type='territory'
                />
              </Tooltip>
            )}
            <Popup>
              <PopupContent
                click={true}
                href={`/app/map/map-analytic/danone-area-profiling?form=${encrypt(
                  JSON.stringify({
                    id_cluster: String(feature.properties.cluster_id),
                    kodepodes: feature.properties.kode_desa,
                  })
                )}`}
                properties={{
                  ...properties,
                  village_code: properties?.village_code
                    ?.join(',')
                    ?.replaceAll(',', ' '),
                  village_code_length: properties?.village_code?.length,
                }}
                type='territory'
              />
            </Popup>
          </Polygon>
        );
      })}
    </>
  );
};

export const DanoneTerritoryGeojson = () => {
  const territoryStore = useTerritoryPlanning();
  const initialMap = useMap();
  const data = territoryStore.state.data;
  const legend = useDanoneLegend();
  const [selectedId, setSelectedId] = useState(null); // Pindahkan ke sini
  useEffect(() => {
    if (!_.isEmpty(data.area) && initialMap) {
      const bounds = L.geoJSON(data.area).getBounds();
      if (bounds.isValid()) {
        initialMap.fitBounds(bounds, {
          paddingTopLeft: [20, 150],
          paddingBottomRight: [20, 20],
        });
      }
    }
  }, [data.area, initialMap]);

  useEffect(() => {
    if (!_.isEmpty(data)) {
      legend.showLegend();
      const territoryLegend = [];
      if (!_.isEmpty(data.marker_rtm)) {
        const categoryAHS = _.uniq(
          data.marker_rtm.map(
            (v) => v.properties?.segment_name ?? 'AHS Reguler'
          )
        );
        Object.entries(markerLegend).forEach(([key, { title, icon }]) => {
          if (categoryAHS.includes(key)) {
            territoryLegend.push({
              id: `poi-${title}`,
              title,
              icon,
            });
          }
        });
      }
      if (!_.isEmpty(data.poi)) {
        const categoryAHS = _.uniq(
          data.poi.map((v) => v.properties?.category ?? 'AHS Reguler')
        );
        Object.entries(markerLegend).forEach(([key, { title, icon }]) => {
          if (categoryAHS.includes(key)) {
            territoryLegend.push({
              id: `poi-${title}`,
              title,
              icon,
            });
          }
        });
      }
      if (!_.isEmpty(data.territory) || !_.isEmpty(data.ses)) {
        const classTerritory = _.uniq(
          data.territory.map((v) => {
            const total = v.properties?.jumlah_ahs;
            return getCategoryTerritory(
              total,
              'low',
              'mediumLow',
              'mediumHigh',
              'high'
            );
          })
        );
        const classSes = _.uniq(
          data.ses.map((v) => {
            const total = v.properties?.jumlah_ahs;
            return getCategoryTerritory(
              total,
              'lowBCCD',
              'mediumLowBCCD',
              'mediumHighBCCD',
              'highBCCD'
            );
          })
        );

        Object.entries(polygonLegend).forEach(([key, { title, icon }]) => {
          if ([...classTerritory, ...classSes].includes(key)) {
            territoryLegend.push({
              id: 'territory' + key,
              title,
              icon,
            });
          }
        });
      }
      if (!_.isEmpty(data.area)) {
        territoryLegend.push({
          id: 'area',
          title: 'Administrative Area',
          icon: areaInfo,
        });
      }
      legend.setItems(territoryLegend);
      legend.setToggleOpen(true);
    }
  }, [data]);

  return (
    <>
      {initialMap && !_.isEmpty(data) && (
        <>
          <DanoneAreaGeojson data={data.area} selectedId={selectedId} />
          <DanoneCatchmentGeojson
            data={data.ses || []}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            style={() => ({
              fillColor: '#757D85',
              fillOpacity: 0.5,
              color: '#757D85',
              weight: 0.4,
            })}
          />
          <DanoneCatchmentGeojson
            data={data.territory || []}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            style={(feature) => ({
              fillColor: getFillColorByHousehold(
                feature.properties?.jumlah_ahs || 0
              ),
              color: getBorderColorByHousehold(
                feature.properties?.jumlah_ahs || 0
              ),
              weight: 2,
              opacity: 1,
              fillOpacity: 0.4,
            })}
          />
          <DanonePOICluster pois={data.marker_rtm || []} />
          <DanonePOICollection
            pois={data.poi || []}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
          />
        </>
      )}
    </>
  );
};
